/**
 * Bond Categorization Engine - Single source of truth for ALL bond categorization logic
 *
 * This module centralizes all bond categorization and classification logic:
 * - Target type identification (Fixed, Overflow, Unlimited)
 * - Flexibility assessment (can accept additional principal)
 * - Collection filtering by category
 * - Distribution strategy determination
 * - Bond state analysis
 *
 * IMPORTANT: All bond categorization in the system MUST use this engine.
 * Do not implement categorization logic elsewhere.
 */

import type { BondSeries, Candidate, Configuration } from '~/types';
import { TargetType as TargetTypeEnum } from '~/types';

/**
 * Bond categorization result structure
 */
export interface BondCategorization {
  fixedTargetBonds: BondSeries[];
  unlimitedBonds: BondSeries[];
  flexibleBonds: BondSeries[];
  totalBonds: number;
  hasTargetedBonds: boolean;
  hasOnlyFixedTargets: boolean;
  hasMixedTargeting: boolean;
}

/**
 * Distribution strategy types based on bond configuration
 */
export type DistributionStrategyType = 'unlimited' | 'all-targeted' | 'mixed-targeting';

/**
 * Ordering strategy types for candidate comparison
 */
export type OrderingStrategyType = 'unlimited' | 'targeted' | 'mixed';

export class BondCategorizationEngine {
  /**
   * Target Type Identification Methods
   */

  /**
   * Checks if a bond has a fixed target constraint (DOES_NOT_EXCEED)
   * These bonds cannot accept more than their target amount
   */
  static isFixedTargetBond(bond: BondSeries): boolean {
    return bond.targetInformation?.targetType === TargetTypeEnum.DOES_NOT_EXCEED;
  }

  /**
   * Checks if a bond has no constraints (unlimited capacity)
   * These bonds have no targetInformation at all
   */
  static isUnlimitedBond(bond: BondSeries): boolean {
    return !bond.targetInformation;
  }

  /**
   * Flexibility Assessment Methods
   */

  /**
   * Checks if a bond can accept additional principal
   * Flexible bonds are unlimited (no target constraints)
   */
  static isFlexibleBond(bond: BondSeries): boolean {
    return BondCategorizationEngine.isUnlimitedBond(bond);
  }

  /**
   * Checks if a bond has any target constraint
   * Returns true for DOES_NOT_EXCEED type
   */
  static hasTargetConstraint(bond: BondSeries): boolean {
    return bond.targetInformation !== undefined;
  }

  /**
   * Collection Filtering Methods
   */

  /**
   * Gets all bonds with fixed target constraints (DOES_NOT_EXCEED)
   */
  static getFixedTargetBonds(bonds: BondSeries[]): BondSeries[] {
    return bonds.filter((bond) => BondCategorizationEngine.isFixedTargetBond(bond));
  }

  /**
   * Gets all bonds with unlimited capacity (no targetInformation)
   */
  static getUnlimitedBonds(bonds: BondSeries[]): BondSeries[] {
    return bonds.filter((bond) => BondCategorizationEngine.isUnlimitedBond(bond));
  }

  /**
   * Gets all flexible bonds (unlimited or accepts overflow)
   */
  static getFlexibleBonds(bonds: BondSeries[]): BondSeries[] {
    return bonds.filter((bond) => BondCategorizationEngine.isFlexibleBond(bond));
  }

  /**
   * Gets bond IDs for all flexible bonds
   */
  static getFlexibleBondIds(bonds: BondSeries[]): string[] {
    return BondCategorizationEngine.getFlexibleBonds(bonds).map((bond) => bond.id);
  }

  /**
   * Analysis Methods
   */

  /**
   * Performs comprehensive categorization of all bonds
   */
  static categorizeBonds(bonds: BondSeries[]): BondCategorization {
    const fixedTargetBonds = BondCategorizationEngine.getFixedTargetBonds(bonds);
    const unlimitedBonds = BondCategorizationEngine.getUnlimitedBonds(bonds);
    const flexibleBonds = BondCategorizationEngine.getFlexibleBonds(bonds);

    return {
      fixedTargetBonds,
      unlimitedBonds,
      flexibleBonds,
      totalBonds: bonds.length,
      hasTargetedBonds: fixedTargetBonds.length > 0,
      hasOnlyFixedTargets: fixedTargetBonds.length === bonds.length,
      hasMixedTargeting: fixedTargetBonds.length > 0 && unlimitedBonds.length > 0,
    };
  }

  /**
   * Checks if any bonds have target information
   */
  static hasTargetedBonds(bonds: BondSeries[]): boolean {
    return bonds.some((bond) => BondCategorizationEngine.hasTargetConstraint(bond));
  }

  /**
   * Checks if all bonds have fixed targets (DOES_NOT_EXCEED only)
   */
  static hasOnlyFixedTargets(bonds: BondSeries[]): boolean {
    return (
      bonds.length > 0 && bonds.every((bond) => BondCategorizationEngine.isFixedTargetBond(bond))
    );
  }

  /**
   * Checks if there's a mix of targeted and flexible bonds
   */
  static hasMixedTargeting(bonds: BondSeries[]): boolean {
    const categorization = BondCategorizationEngine.categorizeBonds(bonds);
    return categorization.hasMixedTargeting;
  }

  /**
   * Distribution Helper Methods
   */

  /**
   * Determines the appropriate distribution strategy based on bond configuration
   */
  static getDistributionStrategy(bonds: BondSeries[]): DistributionStrategyType {
    const categorization = BondCategorizationEngine.categorizeBonds(bonds);

    // All bonds unlimited
    if (!categorization.hasTargetedBonds) {
      return 'unlimited';
    }

    // All bonds have targets (no unlimited bonds)
    if (categorization.unlimitedBonds.length === 0) {
      return 'all-targeted';
    }

    // Mixed scenario
    return 'mixed-targeting';
  }

  /**
   * Orders bonds by distribution priority
   * Priority: Fixed targets first, then unlimited
   */
  static getBondsByPriority(bonds: BondSeries[]): BondSeries[] {
    const categorization = BondCategorizationEngine.categorizeBonds(bonds);
    return [...categorization.fixedTargetBonds, ...categorization.unlimitedBonds];
  }

  /**
   * Candidate-level Helper Methods
   */

  /**
   * Checks if a candidate has any targeted bonds
   */
  static candidateHasTargetedBonds(_candidate: Candidate, configuration: Configuration): boolean {
    if (!configuration.bondSeries || configuration.bondSeries.length === 0) {
      return false;
    }
    return BondCategorizationEngine.hasTargetedBonds(configuration.bondSeries);
  }

  /**
   * Checks if a candidate has flexible bonds
   */
  static candidateHasFlexibleBonds(_candidate: Candidate, configuration: Configuration): boolean {
    if (!configuration.bondSeries || configuration.bondSeries.length === 0) {
      return false;
    }
    return configuration.bondSeries.some((bond) => BondCategorizationEngine.isFlexibleBond(bond));
  }

  /**
   * Gets flexible bond IDs from a candidate
   */
  static getCandidateFlexibleBondIds(
    _candidate: Candidate,
    configuration: Configuration
  ): string[] {
    if (!configuration.bondSeries) {
      return [];
    }
    return BondCategorizationEngine.getFlexibleBondIds(configuration.bondSeries);
  }

  /**
   * Categorizes all bonds in a candidate
   */
  static categorizeCandidateBonds(
    _candidate: Candidate,
    configuration: Configuration
  ): BondCategorization {
    if (!configuration.bondSeries || configuration.bondSeries.length === 0) {
      return {
        fixedTargetBonds: [],
        unlimitedBonds: [],
        flexibleBonds: [],
        totalBonds: 0,
        hasTargetedBonds: false,
        hasOnlyFixedTargets: false,
        hasMixedTargeting: false,
      };
    }
    return BondCategorizationEngine.categorizeBonds(configuration.bondSeries);
  }

  /**
   * Ordering Strategy Methods
   */

  /**
   * Determines the appropriate candidate ordering strategy based on bond mix
   */
  static determineOrderingStrategy(
    candidates: Candidate[],
    configuration: Configuration
  ): OrderingStrategyType {
    if (candidates.length === 0) return 'unlimited';

    // Use the first candidate as representative (all candidates have same bond mix)
    const candidate = candidates[0];
    const categorization = BondCategorizationEngine.categorizeCandidateBonds(
      candidate,
      configuration
    );

    if (!categorization.hasTargetedBonds) {
      return 'unlimited';
    } else if (categorization.unlimitedBonds.length === 0) {
      return 'targeted';
    } else {
      return 'mixed';
    }
  }

  /**
   * Gets a human-readable description of the ordering strategy
   */
  static getOrderingStrategyDescription(strategy: OrderingStrategyType): string {
    switch (strategy) {
      case 'unlimited':
        return 'Unlimited Strategy: Viability → Value → Utilization';
      case 'targeted':
        return 'Targeted Strategy: Viability → Efficiency';
      case 'mixed':
        return 'Mixed Strategy: Viability → Value → Efficiency';
      default:
        return 'Unknown Strategy';
    }
  }

  /**
   * Summary and Debug Methods
   */

  /**
   * Gets a human-readable category name for a bond
   */
  static getBondCategory(bond: BondSeries): string {
    if (BondCategorizationEngine.isFixedTargetBond(bond)) return 'Fixed Target';
    if (BondCategorizationEngine.isUnlimitedBond(bond)) return 'Unlimited';
    return 'Unknown';
  }

  /**
   * Creates a summary string of bond categorization
   */
  static getCategorySummary(bonds: BondSeries[]): string {
    const categorization = BondCategorizationEngine.categorizeBonds(bonds);
    return (
      `Fixed: ${categorization.fixedTargetBonds.length}, ` +
      `Unlimited: ${categorization.unlimitedBonds.length}`
    );
  }
}
